import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Upload, Trash2, Plus, X, FileImage, FileCode, Loader2, HardDrive, LogIn } from 'lucide-react';
import { useToast } from '../../hooks/use-toast';

import { parseSvgForEditor, extractSvgDimensions, DEFAULT_STROKE, DEFAULT_GRADIENT } from './svgUtils';
import { listUploads, uploadFile, deleteUpload, getQuota, formatBytes, UploadsApiError } from '../../lib/uploadsApi';
import { isTokenExpired } from '../../utils/auth';

const AUTH_TOKEN_KEY = 'ivisora_auth_token';
const DESIGNER_TOKEN_KEY = 'user_token';

/**
 * Decide whether the user has a usable session BEFORE we hit the API.
 *
 * Returns the auth state in three buckets:
 *   'authenticated' — token present and not expired (per the local `exp` claim)
 *   'expired'       — token present but `exp` is in the past; treat like a fresh
 *                     session ask but clear the stale token first so the next
 *                     load doesn't repeat the song-and-dance
 *   'anonymous'     — no token at all; user just hasn't signed in
 *
 * The 'expired' / 'anonymous' split lets us word the CTA correctly without
 * making the user feel like something broke when nothing did.
 */
const _readAuthState = () => {
  const tok = localStorage.getItem(AUTH_TOKEN_KEY) || localStorage.getItem(DESIGNER_TOKEN_KEY);
  if (!tok) return 'anonymous';
  if (isTokenExpired(tok)) {
    // Clear once so we don't drag the dead token forward.
    localStorage.removeItem(AUTH_TOKEN_KEY);
    localStorage.removeItem(DESIGNER_TOKEN_KEY);
    return 'expired';
  }
  return 'authenticated';
};

// Allowed types mirror backend utils/upload_validation.py. Keep these in sync.
const IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/webp'];
const SVG_TYPE = 'image/svg+xml';
const ACCEPTED_TYPES = [...IMAGE_TYPES, SVG_TYPE];

// Per-file caps the user sees in the helper text. Backend is the actual gate.
const IMAGE_MAX_MB = 25;
const SVG_MAX_MB = 10;

const PAGE_SIZE = 24;

/**
 * Backend-authoritative upload library.
 *
 * Source of truth = GET /api/uploads (paginated, soft-delete-aware).
 * localStorage is intentionally NOT used — uploads now survive refresh, login
 * change, and browser swap because the bytes + the manifest both live in the
 * cloud.
 *
 * SVG specifics:
 *   - The file itself is uploaded as-is to Supabase via the standard pipeline.
 *   - We do NOT store the SVG XML in the database (would bloat the row + need
 *     a migration the day someone uploads a 10 MB SVG). Instead, the colorMap
 *     used by the editor's recolor controls is computed lazily: the FIRST time
 *     a user adds an SVG to the canvas, we fetch its URL once, parse it, and
 *     cache the parsed colors in a per-session WeakMap-equivalent (Map keyed
 *     by upload id). The thumbnail itself is rendered via plain <img src> so
 *     no fetch is needed just to draw the panel.
 */
const _svgCache = new Map(); // id -> { svgContent, colors, colorMap }

const UploadPanel = ({ onAddElement, onClose, onPickImage }) => {
  const { toast } = useToast();
  const fileInputRef = useRef(null);

  // ── Auth state (drives whether we hit the API at all) ───────────────────────
  // Tracked separately from list state so we render a calm "sign in" view
  // instead of throwing a noisy "Session expired" toast for users who simply
  // haven't signed in yet — a common case on a public design tool.
  const [authState, setAuthState] = useState(_readAuthState);

  // ── Library state ───────────────────────────────────────────────────────────
  const [items, setItems] = useState([]);
  const [cursor, setCursor] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const [loadingList, setLoadingList] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [listError, setListError] = useState(null);

  // ── Quota state ─────────────────────────────────────────────────────────────
  const [quota, setQuota] = useState(null); // { used_bytes, total_bytes, plan }

  // ── Per-file upload progress: { [tempId]: { name, progress 0..1 } } ─────────
  const [activeUploads, setActiveUploads] = useState({});
  const [isDragging, setIsDragging] = useState(false);

  // Centralised reaction to a 401 coming back from any endpoint. Flipping
  // authState to 'expired' lets the unauth view take over without any toast.
  // Clearing tokens here makes sure a refresh doesn't repeat the dance.
  const handleAuthFailure = useCallback(() => {
    localStorage.removeItem(AUTH_TOKEN_KEY);
    localStorage.removeItem(DESIGNER_TOKEN_KEY);
    setAuthState('expired');
    setItems([]);
    setQuota(null);
    setCursor(null);
    setHasMore(false);
  }, []);

  // ── Initial hydrate (auth-gated) ────────────────────────────────────────────
  const refreshQuota = useCallback(async () => {
    if (authState !== 'authenticated') return;
    try {
      const q = await getQuota();
      setQuota(q);
    } catch (e) {
      if (e instanceof UploadsApiError && e.code === 'unauthorized') {
        handleAuthFailure();
      }
      // Otherwise: quota is best-effort; failing to fetch it shouldn't break the panel.
    }
  }, [authState, handleAuthFailure]);

  const loadFirstPage = useCallback(async () => {
    if (authState !== 'authenticated') return;
    setLoadingList(true);
    setListError(null);
    try {
      const { items: rows, next_cursor, has_more } = await listUploads({ limit: PAGE_SIZE });
      setItems(rows);
      setCursor(next_cursor);
      setHasMore(has_more);
    } catch (e) {
      if (e instanceof UploadsApiError && e.code === 'unauthorized') {
        handleAuthFailure();
        return;
      }
      const msg = e instanceof UploadsApiError ? e.message : 'Could not load your uploads.';
      setListError(msg);
    } finally {
      setLoadingList(false);
    }
  }, [authState, handleAuthFailure]);

  useEffect(() => {
    if (authState === 'authenticated') {
      loadFirstPage();
      refreshQuota();
    }
  }, [authState, loadFirstPage, refreshQuota]);

  const loadMore = useCallback(async () => {
    if (!hasMore || loadingMore || !cursor) return;
    setLoadingMore(true);
    try {
      const { items: rows, next_cursor, has_more } = await listUploads({
        cursor,
        limit: PAGE_SIZE,
      });
      setItems(prev => [...prev, ...rows]);
      setCursor(next_cursor);
      setHasMore(has_more);
    } catch (e) {
      const msg = e instanceof UploadsApiError ? e.message : 'Could not load more uploads.';
      toast({ title: 'Load failed', description: msg, variant: 'destructive' });
    } finally {
      setLoadingMore(false);
    }
  }, [cursor, hasMore, loadingMore, toast]);

  // ── Upload flow ─────────────────────────────────────────────────────────────
  const processFiles = useCallback((files) => {
    if (authState !== 'authenticated') {
      // Quietly route the user to sign in instead of round-tripping for a 401.
      handleAuthFailure();
      return;
    }
    const fileArr = Array.from(files);
    fileArr.forEach(async (file) => {
      // Client-side pre-check: friendly fast-fail before bytes hit the wire.
      // Backend remains the authority — these messages match its limits.
      if (!ACCEPTED_TYPES.includes(file.type)) {
        toast({
          title: 'Unsupported file',
          description: `${file.name} — only PNG, JPEG, WebP, SVG are supported.`,
          variant: 'destructive',
        });
        return;
      }
      const isSvg = file.type === SVG_TYPE;
      const maxBytes = (isSvg ? SVG_MAX_MB : IMAGE_MAX_MB) * 1024 * 1024;
      if (file.size > maxBytes) {
        toast({
          title: 'File too large',
          description: `${file.name} exceeds ${isSvg ? SVG_MAX_MB : IMAGE_MAX_MB} MB.`,
          variant: 'destructive',
        });
        return;
      }

      const tempId = `tmp-${Date.now()}-${Math.random().toString(36).slice(2)}`;
      setActiveUploads(prev => ({ ...prev, [tempId]: { name: file.name, progress: 0 } }));

      try {
        const row = await uploadFile(file, (loaded, total) => {
          if (!total) return;
          setActiveUploads(prev => ({
            ...prev,
            [tempId]: { name: file.name, progress: loaded / total },
          }));
        });
        // Optimistically prepend so the user sees their file instantly.
        setItems(prev => [row, ...prev]);
        refreshQuota();
        toast({ title: 'Uploaded', description: file.name });
      } catch (e) {
        const isAuth = e instanceof UploadsApiError && e.code === 'unauthorized';
        if (isAuth) {
          // No toast — just transition to the unauth view. Less alarming
          // than a destructive popup when a token just rolled over.
          handleAuthFailure();
        } else {
          const msg = e instanceof UploadsApiError ? e.message : 'Upload failed.';
          const isQuota = e instanceof UploadsApiError && e.code === 'quota_exceeded';
          toast({
            title: isQuota ? 'Storage full' : 'Upload failed',
            description: msg,
            variant: 'destructive',
          });
        }
      } finally {
        setActiveUploads(prev => {
          const next = { ...prev };
          delete next[tempId];
          return next;
        });
      }
    });
  }, [refreshQuota, toast]);

  // ── DnD handlers ────────────────────────────────────────────────────────────
  const handleDragEnter = (e) => {
    e.preventDefault(); e.stopPropagation();
    if (e.dataTransfer.types.includes('Files')) setIsDragging(true);
  };
  const handleDragOver = (e) => { e.preventDefault(); e.stopPropagation(); };
  const handleDragLeave = (e) => {
    e.preventDefault(); e.stopPropagation();
    if (!e.currentTarget.contains(e.relatedTarget)) setIsDragging(false);
  };
  const handleDrop = (e) => {
    e.preventDefault(); e.stopPropagation();
    setIsDragging(false);
    if (e.dataTransfer.files.length > 0) processFiles(e.dataTransfer.files);
  };
  const handleFileInput = (e) => {
    processFiles(e.target.files);
    e.target.value = '';
  };

  // ── Delete ──────────────────────────────────────────────────────────────────
  const handleDelete = useCallback(async (item) => {
    const prev = items;
    // Optimistic removal — restore on error.
    setItems(rows => rows.filter(r => r.id !== item.id));
    try {
      await deleteUpload(item.id);
      refreshQuota();
    } catch (e) {
      setItems(prev);
      if (e instanceof UploadsApiError && e.code === 'unauthorized') {
        handleAuthFailure();
        return;
      }
      const msg = e instanceof UploadsApiError ? e.message : 'Delete failed.';
      toast({ title: 'Delete failed', description: msg, variant: 'destructive' });
    }
  }, [items, refreshQuota, toast, handleAuthFailure]);

  // ── Add to canvas ───────────────────────────────────────────────────────────
  const handleAddToCanvas = useCallback(async (item) => {
    // Pick mode: a Replace-image flow is active — hand the URL to the editor
    // instead of adding a new element. onPickImage returns true when consumed.
    if (item.kind !== 'svg' && onPickImage && onPickImage(item.url)) return;
    if (item.kind === 'svg') {
      // Lazy-fetch + parse so the panel itself doesn't pay for it on every load.
      let parsed = _svgCache.get(item.id);
      if (!parsed) {
        try {
          const resp = await fetch(item.url);
          const text = await resp.text();
          parsed = parseSvgForEditor(text);
          _svgCache.set(item.id, parsed);
        } catch {
          toast({ title: 'SVG load failed', description: 'Could not fetch the SVG content.', variant: 'destructive' });
          return;
        }
      }
      const { w, h } = extractSvgDimensions(parsed.svgContent);
      const ratio = w / h;
      const MAX = 300;
      const canvasW = ratio >= 1 ? MAX : Math.round(MAX * ratio);
      const canvasH = ratio >= 1 ? Math.round(MAX / ratio) : MAX;
      onAddElement({
        type: 'svg',
        x: 50, y: 50,
        width: canvasW, height: canvasH,
        svgContent: parsed.svgContent,
        colors: [...(parsed.colors || [])],
        colorMap: { ...(parsed.colorMap || {}) },
        fill: parsed.colors?.[0] || '#000000',
        gradient: { ...DEFAULT_GRADIENT },
        stroke: { ...DEFAULT_STROKE },
        rotation: 0,
        opacity: 1,
      });
      toast({ title: 'SVG added to canvas' });
      return;
    }

    const img = new window.Image();
    img.onload = () => {
      onAddElement({
        type: 'image', src: item.url, x: 50, y: 50,
        width: Math.min(img.naturalWidth, 1200),
        height: Math.min(img.naturalHeight, 900),
      });
      toast({ title: 'Image added to canvas' });
    };
    img.onerror = () => {
      onAddElement({ type: 'image', src: item.url, x: 50, y: 50, width: 800, height: 600 });
    };
    img.src = item.url;
  }, [onAddElement, onPickImage, toast]);

  // ── Thumbnail renderer ──────────────────────────────────────────────────────
  const renderThumb = (item) => {
    if (item.kind === 'svg') {
      // Render the SVG file directly. Browsers handle <img src=...svg> safely
      // (no script execution, just paint). For panel preview we don't need the
      // recolor pipeline — that kicks in only at add-to-canvas time.
      return (
        <div
          className="w-full h-full bg-[repeating-conic-gradient(#e5e7eb_0%_25%,#fff_0%_50%)] bg-[length:8px_8px]"
        >
          <img
            src={item.url}
            alt={item.name}
            className="w-full h-full object-contain"
            draggable={false}
          />
        </div>
      );
    }
    return (
      <img
        src={item.thumbnail_url || item.url}
        alt={item.name}
        className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
        draggable={false}
      />
    );
  };

  // ── Derived quota values ────────────────────────────────────────────────────
  const pct = quota && quota.total_bytes > 0
    ? Math.min(100, Math.round((quota.used_bytes / quota.total_bytes) * 100))
    : 0;
  const nearFull = pct >= 90;
  const planLabel = quota?.plan ? quota.plan.charAt(0).toUpperCase() + quota.plan.slice(1) : '—';

  const inFlightCount = Object.keys(activeUploads).length;

  // ── Sign-in CTA: navigate to home where the AuthModal lives (Header.jsx) ──
  // ?signin=1 is a soft hint the home page can read to auto-open the login
  // modal; if it isn't wired, the user still lands somewhere they can sign in.
  const goSignIn = () => {
    window.location.assign('/?signin=1');
  };

  // ── Render ──────────────────────────────────────────────────────────────────
  return (
    <div className="h-full flex flex-col bg-white select-none">

      {/* Header */}
      <div className="px-4 pt-4 pb-3 border-b border-gray-100 shrink-0">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-violet-500 to-fuchsia-500 flex items-center justify-center shadow-sm">
              <Upload className="w-3.5 h-3.5 text-white" />
            </div>
            <div>
              <h3 className="font-semibold text-gray-900 text-sm leading-tight">Uploads</h3>
              <p className="text-[10px] text-gray-400 leading-tight">Images &amp; SVG vectors</p>
            </div>
          </div>
          {onClose && (
            <button onClick={onClose} className="w-7 h-7 rounded-lg hover:bg-gray-100 flex items-center justify-center transition-colors text-gray-400 hover:text-gray-600">
              <X className="w-4 h-4" />
            </button>
          )}
        </div>

        {/* Storage bar — hidden for unauthenticated users since there's nothing
            to show yet (no plan, no usage); avoids a "—" / "Loading…" flicker. */}
        {authState === 'authenticated' && (
          <div className="mt-3">
            <div className="flex items-center justify-between mb-1">
              <div className="flex items-center gap-1.5 text-[10px] text-gray-500">
                <HardDrive className="w-3 h-3" />
                <span className="font-medium">
                  {quota ? `${formatBytes(quota.used_bytes)} / ${formatBytes(quota.total_bytes)}` : 'Loading…'}
                </span>
              </div>
              <span className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider">
                {planLabel}
              </span>
            </div>
            <div className="h-1.5 rounded-full bg-gray-100 overflow-hidden">
              <div
                className={`h-full rounded-full transition-all duration-500 ${nearFull
                  ? 'bg-gradient-to-r from-amber-400 to-rose-500'
                  : 'bg-gradient-to-r from-violet-500 to-fuchsia-500'}`}
                style={{ width: `${pct}%` }}
              />
            </div>
            {nearFull && (
              <p className="text-[10px] text-rose-500 mt-1 font-medium">
                You're using {pct}% of your storage. Upgrade for more space.
              </p>
            )}
          </div>
        )}
      </div>

      {/* Scrollable body */}
      <div className="flex-1 overflow-y-auto min-h-0">
        {authState !== 'authenticated' ? (
          // ── Unauthenticated state ─────────────────────────────────────────
          // Calm, non-destructive. Tells the user what signing in unlocks and
          // gives them a single clear button. The wording adapts to whether
          // they have a stale token ('expired') vs never signed in.
          <div className="p-6 flex flex-col items-center justify-center text-center min-h-[300px]">
            <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-violet-100 to-fuchsia-100 flex items-center justify-center mb-4 shadow-sm">
              <LogIn className="w-7 h-7 text-violet-500" />
            </div>
            <h4 className="text-sm font-semibold text-gray-900">
              {authState === 'expired' ? 'Please sign in again' : 'Sign in to upload'}
            </h4>
            <p className="text-[11px] text-gray-500 mt-1.5 max-w-[240px] leading-relaxed">
              {authState === 'expired'
                ? 'Your session ended. Sign in to keep using your upload library.'
                : 'Your upload library is private to your account — sign in to save and reuse files across devices.'}
            </p>
            <button
              onClick={goSignIn}
              className="mt-5 px-5 py-2 rounded-lg bg-gradient-to-r from-violet-600 to-fuchsia-500 hover:from-violet-700 hover:to-fuchsia-600 text-white text-xs font-semibold shadow-sm transition-all"
            >
              Sign in
            </button>
            <p className="mt-4 text-[10px] text-gray-400">
              Free plan includes 500 MB of storage
            </p>
          </div>
        ) : (
        <div className="p-4 space-y-4">

          {/* Drop zone */}
          <div
            onDragEnter={handleDragEnter} onDragOver={handleDragOver}
            onDragLeave={handleDragLeave} onDrop={handleDrop}
            onClick={() => inFlightCount === 0 && fileInputRef.current?.click()}
            className={`relative flex flex-col items-center justify-center gap-3 min-h-[160px] rounded-2xl border-2 border-dashed transition-all duration-200 ${inFlightCount > 0 ? 'border-violet-400 bg-violet-50 cursor-wait' : 'cursor-pointer'} ${inFlightCount === 0 && (isDragging ? 'border-violet-500 bg-violet-50 scale-[0.98]' : 'border-gray-200 bg-gray-50 hover:border-violet-400 hover:bg-violet-50/50')}`}
          >
            <div className={`w-14 h-14 rounded-full flex items-center justify-center transition-all duration-200 ${isDragging ? 'bg-violet-200 scale-110' : 'bg-violet-100'}`}>
              {inFlightCount > 0
                ? <Loader2 className="w-6 h-6 text-violet-500 animate-spin" />
                : isDragging
                  ? <Plus className="w-7 h-7 text-violet-600" />
                  : <Upload className="w-6 h-6 text-violet-500" />}
            </div>
            <div className="text-center px-4">
              <p className={`text-sm font-semibold transition-colors ${inFlightCount > 0 ? 'text-violet-600' : isDragging ? 'text-violet-700' : 'text-gray-700'}`}>
                {inFlightCount > 0
                  ? `Uploading ${inFlightCount} file${inFlightCount > 1 ? 's' : ''}…`
                  : isDragging ? 'Drop to upload' : 'Click to upload'}
              </p>
              {inFlightCount === 0 && <p className="text-[11px] text-gray-400 mt-0.5">or drag &amp; drop here</p>}
              {inFlightCount === 0 && (
                <p className="text-[10px] text-gray-300 mt-1">
                  PNG, JPG, WebP &middot; up to {IMAGE_MAX_MB}MB &middot; <span className="text-violet-400 font-medium">SVG</span> up to {SVG_MAX_MB}MB
                </p>
              )}
            </div>
            {isDragging && inFlightCount === 0 && <div className="absolute inset-0 rounded-2xl border-2 border-violet-500 animate-pulse pointer-events-none" />}
            <input
              ref={fileInputRef}
              type="file"
              accept={ACCEPTED_TYPES.join(',')}
              multiple
              onChange={handleFileInput}
              className="hidden"
              data-testid="upload-input"
            />
          </div>

          {/* In-flight upload progress */}
          {inFlightCount > 0 && (
            <div className="space-y-2">
              {Object.entries(activeUploads).map(([tid, info]) => (
                <div key={tid} className="rounded-xl border border-violet-100 bg-violet-50/40 px-3 py-2">
                  <div className="flex items-center gap-2 mb-1.5">
                    <Loader2 className="w-3 h-3 text-violet-500 animate-spin shrink-0" />
                    <span className="text-[11px] text-gray-700 truncate flex-1">{info.name}</span>
                    <span className="text-[10px] text-gray-400 font-medium">{Math.round(info.progress * 100)}%</span>
                  </div>
                  <div className="h-1 rounded-full bg-violet-100 overflow-hidden">
                    <div
                      className="h-full rounded-full bg-gradient-to-r from-violet-500 to-fuchsia-500 transition-all duration-200"
                      style={{ width: `${Math.round(info.progress * 100)}%` }}
                    />
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Library */}
          {loadingList ? (
            <div className="flex flex-col items-center justify-center py-10 gap-3 text-center">
              <Loader2 className="w-6 h-6 text-violet-400 animate-spin" />
              <p className="text-xs text-gray-400">Loading your uploads…</p>
            </div>
          ) : listError ? (
            <div className="flex flex-col items-center justify-center py-10 gap-3 text-center">
              <div className="w-14 h-14 rounded-2xl bg-rose-50 flex items-center justify-center">
                <X className="w-7 h-7 text-rose-300" />
              </div>
              <div>
                <p className="text-sm font-medium text-gray-600">{listError}</p>
                <button onClick={loadFirstPage} className="text-xs text-violet-600 font-medium hover:text-violet-700 mt-1.5">
                  Try again
                </button>
              </div>
            </div>
          ) : items.length > 0 ? (
            <div>
              <div className="flex items-center justify-between mb-2.5">
                <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">
                  Your Uploads ({items.length}{hasMore ? '+' : ''})
                </p>
              </div>

              <div className="grid grid-cols-2 gap-2">
                {items.map((item) => (
                  <div
                    key={item.id}
                    className="group relative rounded-xl overflow-hidden border border-gray-100 shadow-sm bg-gray-50 cursor-pointer"
                    style={{ aspectRatio: '1' }}
                    onClick={() => handleAddToCanvas(item)}
                    data-testid={`upload-thumb-${item.id}`}
                    draggable={item.kind !== 'svg'}
                    onDragStart={(e) => {
                      if (item.kind !== 'svg') {
                        e.dataTransfer.setData('application/x-editor-image-src', item.url);
                        e.dataTransfer.effectAllowed = 'copy';
                      }
                    }}
                  >
                    {renderThumb(item)}

                    {item.kind === 'svg' && (
                      <div className="absolute top-1.5 left-1.5 flex items-center gap-0.5 bg-violet-600/90 text-white rounded-md px-1.5 py-0.5">
                        <FileCode className="w-2.5 h-2.5" />
                        <span className="text-[8px] font-bold tracking-wide">SVG</span>
                      </div>
                    )}

                    <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-all duration-200 flex flex-col justify-between p-2">
                      <div className="flex justify-end">
                        <button
                          onClick={(e) => { e.stopPropagation(); handleDelete(item); }}
                          className="w-6 h-6 rounded-full bg-red-500/90 hover:bg-red-600 text-white flex items-center justify-center transition-colors shadow-md"
                          title="Remove"
                        >
                          <Trash2 className="w-3 h-3" />
                        </button>
                      </div>
                      <div>
                        <div className="flex items-center gap-1 mb-1">
                          <div className="flex items-center gap-1 bg-white/20 backdrop-blur-sm rounded-md px-1.5 py-0.5">
                            <Plus className="w-3 h-3 text-white" />
                            <span className="text-white text-[10px] font-medium">Add to canvas</span>
                          </div>
                        </div>
                        <p className="text-white/80 text-[9px] truncate">{item.name}</p>
                        <p className="text-white/50 text-[8px]">{formatBytes(item.bytes)}</p>
                      </div>
                    </div>
                  </div>
                ))}

                {/* Upload more tile */}
                <button
                  onClick={() => fileInputRef.current?.click()}
                  className="aspect-square rounded-xl border-2 border-dashed border-gray-200 hover:border-violet-400 bg-gray-50 hover:bg-violet-50 flex flex-col items-center justify-center gap-1 transition-all group"
                  title="Upload more"
                >
                  <div className="w-8 h-8 rounded-full bg-gray-100 group-hover:bg-violet-100 flex items-center justify-center transition-colors">
                    <Plus className="w-4 h-4 text-gray-400 group-hover:text-violet-500 transition-colors" />
                  </div>
                  <span className="text-[10px] text-gray-400 group-hover:text-violet-500 transition-colors">Upload more</span>
                </button>
              </div>

              {/* Load more */}
              {hasMore && (
                <button
                  onClick={loadMore}
                  disabled={loadingMore}
                  className="mt-3 w-full py-2 rounded-lg border border-gray-200 hover:border-violet-400 hover:bg-violet-50/50 text-xs font-medium text-gray-600 hover:text-violet-600 transition-all disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {loadingMore ? <Loader2 className="w-3 h-3 animate-spin" /> : null}
                  {loadingMore ? 'Loading…' : 'Load more'}
                </button>
              )}
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center py-10 gap-3 text-center">
              <div className="w-14 h-14 rounded-2xl bg-gray-100 flex items-center justify-center">
                <FileImage className="w-7 h-7 text-gray-300" />
              </div>
              <div>
                <p className="text-sm font-medium text-gray-500">No uploads yet</p>
                <p className="text-xs text-gray-400 mt-0.5">Upload images or SVG vectors</p>
              </div>
            </div>
          )}
        </div>
        )}
      </div>

      {/* Footer */}
      <div className="shrink-0 px-4 py-2 border-t border-gray-100 bg-gray-50/60">
        <p className="text-[10px] text-gray-400 text-center">
          Files are stored securely &middot; SVG colors editable after placing on canvas
        </p>
      </div>
    </div>
  );
};

export default UploadPanel;
